/**
 * The front page — `docs/SCREENS.md` §1.
 *
 * The catalog is static config. The proof points are NOT: they read live counts, because a
 * client-facing page whose numbers are hardcoded is a brochure, and the first prospect to compare
 * it with the dashboard stops trusting both.
 *
 * Every card also carries whether it is BUILT. The prototype's fourteen cards are all presented as
 * available; here, four of them are phase 3–4 and say so. That is a deliberate departure from the
 * UX source of truth, in the one direction it is always safe to depart: towards not claiming
 * something that is not true.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { CAPABILITIES, type Availability } from "@hub/core/browser";
import { useApi } from "../lib/api";
import { Card, Pill } from "../components";

interface DashboardShape {
  canAct: { humans: number; agents: number };
  agentCredentials: { shortLivedPct: number | null; total: number };
  riskPaths: number;
  needsDecision: { orphanedAgents: number; approvals: number };
}

const FILTERS: { key: Availability | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "core", label: "Core" },
  { key: "addon", label: "Add-on" },
  { key: "soon", label: "Coming soon" },
];

export function Capabilities() {
  const [filter, setFilter] = useState<Availability | "all">("all");
  const { data, error } = useApi<DashboardShape>("/v1/dashboard");

  const shown = CAPABILITIES.filter((c) => filter === "all" || c.availability === filter);

  return (
    <>
      <header style={{ padding: "24px 0 8px", maxWidth: "64ch" }}>
        <h1 style={{ fontSize: 40, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
          Every identity that can act — human or agent — under one control plane.
        </h1>
        <p className="sub">
          Your people and your agents get the same lifecycle, the same review and the same policy
          engine. Different credential mechanics; one set of evidence. It sits on top of the identity,
          privileged-access and visibility systems you already run.
        </p>
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <Link className="btn primary" to="/dashboard">Open the dashboard</Link>
          <a className="btn" href="#catalog">Browse capabilities</a>
        </div>
      </header>

      {/* THE PROOF POINTS READ LIVE. A number here that came from a constant would be a claim about
          a customer's estate made by a marketing page. */}
      <div className="grid k4" style={{ margin: "26px 0" }}>
        <Proof label="Identities governed" value={
          data ? (data.canAct.humans + data.canAct.agents).toLocaleString() : error ? "—" : "…"
        } note={data ? `${data.canAct.humans.toLocaleString()} people · ${data.canAct.agents.toLocaleString()} agents` : ""} />
        <Proof label="Agent credentials short-lived" value={
          data?.agentCredentials.shortLivedPct === null
            ? "—"
            : data ? `${data.agentCredentials.shortLivedPct}%` : "…"
        } note={
          data?.agentCredentials.shortLivedPct === null
            ? "no agent credentials yet — not the same as full compliance"
            : "target is above 95%"
        } />
        <Proof label="Standing grants onto sensitive data" value={data ? String(data.riskPaths) : "…"}
               note="people and agents counted together" />
        <Proof label="Agents with no accountable owner" value={data ? String(data.needsDecision.orphanedAgents) : "…"}
               note="enforced by a database constraint, not a report" />
      </div>

      <section id="catalog">
        <div className="page-head">
          <h3 style={{ fontSize: 18 }}>Capability catalog</h3>
          <div className="chips">
            {FILTERS.map((f) => (
              <button key={f.key} className={`chip ${filter === f.key ? "on" : ""}`}
                      onClick={() => setFilter(f.key)}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid k3">
          {shown.map((c) => {
            const body = (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                  <strong style={{ fontSize: 14 }}>{c.name}</strong>
                  <Pill tone={c.availability === "core" ? "ok" : c.availability === "addon" ? "dim" : "warn"}>
                    {c.availability === "core" ? "Core" : c.availability === "addon" ? "Add-on" : "Coming soon"}
                  </Pill>
                </div>
                <p className="sub" style={{ fontSize: 12.5, marginTop: 8 }}>{c.description}</p>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, fontSize: 12, color: "var(--muted)" }}>
                  <span>{c.group}</span>
                  <span style={{ color: c.phase <= 2 ? "var(--accent)" : "var(--dim)" }}>
                    {c.phase <= 2 ? (c.route ? "See it →" : "Configured at onboarding") : `Phase ${c.phase}`}
                  </span>
                </div>
              </>
            );
            return c.route && c.phase <= 2
              ? <Link key={c.id} to={c.route} className="card" style={{ color: "inherit" }} data-testid="capability">{body}</Link>
              : <div key={c.id} className="card" data-testid="capability">{body}</div>;
          })}
        </div>
      </section>

      <div style={{ marginTop: 26 }}>
        <Card title="How this is engaged">
          <p className="sub" style={{ marginTop: 0 }}>
            The hub orchestrates rather than replaces. It reads your identity provider (Ping or Entra)
            and your entitlement graph, and it asks your privileged-access vendor (Britive, Akeyless
            or CyberArk) to mint and revoke. Your systems of record stay where they are; what is new
            is one place where a human and an agent are governed by the same rules, and one evidence
            store that can prove it.
          </p>
        </Card>
      </div>
    </>
  );
}

function Proof({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="card kpi">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {note && <div className="delta">{note}</div>}
    </div>
  );
}
